import { useId, type HTMLAttributes, type ReactNode } from 'react'
import { AlertCircle, AlertTriangle, Info } from 'lucide-react'
import { CountPill } from '../controls/CountPill'
import { EmptyState } from '../controls/EmptyState'
import { IconTile, type IconTileTone } from '../controls/IconTile'
import { Skeleton } from '../controls/Skeleton'
import { cx } from '../controls/cx'
import s from './DashboardCard.module.css'

/** Where the card's CONTENT is. `ready` draws the body the host passed. */
export type DashboardCardState = 'ready' | 'loading' | 'empty'
export type DashboardCardTone = 'info' | 'warn' | 'critical'

export interface DashboardCardProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  /** The card's name — the heading the section is labelled by. */
  title: ReactNode
  /** ONE muted line under the title: what the card is about ("Per environment,
   *  by deployment kind"). In the attention variant it is the sentence. */
  sub?: ReactNode
  /** The head's mark, drawn in the 26px brand tile. Decorative. */
  icon?: ReactNode
  /**
   * The head tile's tone. `brand` is every card's mark; a SPACE's card takes
   * its categorical tone (#2502, `SpaceTile`), which is data, never a literal.
   */
  iconTone?: IconTileTone
  /**
   * THE WHOLE-CARD LINK (#2502 §5.4). The card's TITLE becomes its one control
   * — a link to `href`, or a button running `onOpen` — and that control's
   * `::after` is stretched over the slab, so a click anywhere on the card opens
   * it while a reader hears only the title. Head actions and the foot's
   * controls sit above the overlay and stay their own. With both, `onOpen`
   * runs on the link's click (a router's navigate).
   */
  href?: string
  onOpen?: () => void
  /** A count for the head, drawn as `CountPill` — a figure, never a `Badge`. */
  count?: ReactNode
  /** Head actions at the right: a ghost ⋮, a small secondary ("View all"). */
  actions?: ReactNode
  /** The foot, behind a joint: secondary then primary, in the order given. */
  footer?: ReactNode
  /**
   * `standard` is every card with a head and a body. `attention` is the one
   * card with a 3px left cut in its state colour — the only place a left
   * accent is allowed — a state-tinted mark, title + sentence and actions right.
   */
  variant?: 'standard' | 'attention'
  /** The attention card's state. Ignored by `standard`. */
  tone?: DashboardCardTone
  /**
   * `loading`: skeleton rows inside the recess, in the success geometry, so the
   * card does not jump when the content lands. `empty`: a recess that says what
   * WILL appear here, with at most one action. Anything else — a refusal, an
   * outage — is content the HOST renders as the body (`ErrorState`,
   * `QueryBoundary`), because an error dressed as "nothing yet" is a lie.
   */
  state?: DashboardCardState
  empty?: { title: ReactNode; description?: ReactNode; action?: ReactNode }
  /** How many skeleton rows the loading recess draws. */
  loadingRows?: number
  /** The heading level. `3` under a page section's `h2`, which is where cards sit. */
  headingLevel?: 2 | 3 | 4
  testId?: string
  children?: ReactNode
}

/** One mark per state, three DIFFERENT marks — the reason `Notice` gives:
 *  two states drawn with one glyph are one state to a reader. */
const MARK: Record<DashboardCardTone, ReactNode> = {
  info: <Info size={16} aria-hidden />,
  warn: <AlertTriangle size={16} aria-hidden />,
  critical: <AlertCircle size={16} aria-hidden />,
}

/** Skeleton line widths: uneven on purpose, so a loading card reads as rows of
 *  text rather than as a striped placeholder. */
const LINE = ['72%', '88%', '56%', '80%', '64%']

/**
 * THE DASHBOARD CARD (#2406, "Dashboards and Cards" — the eight variants).
 *
 * *Every card is a slab; every container inside one is a recess; every record
 * inside a container is a plate.* This is the slab and its three fixed parts —
 * the head (26px brand mark · title · one-line muted sub · count · actions
 * right), the body, and the foot behind a joint — and the two states every card
 * has before it has content. The eight variants are NOT eight components:
 *
 *   metric · single / with trend   `StatTile` (a slab of its own), with its
 *                                  `unit`, `delta` and `trend`
 *   records                        this card + `CardRecords` (recess → plates)
 *   progress                       this card + `CardProgress` (channels of `Meter`)
 *   attention                      `variant="attention"` + `tone`
 *   empty                          `state="empty"` (a recessed `EmptyState`)
 *   loading                        `state="loading"` (skeleton rows in the recess)
 *   footer action                  `footer`
 *
 * so there is ONE slab, one head and one foot in the kit, and a card that is
 * both "records" and "footer action" is simply both.
 *
 * It fetches nothing and knows no measure. A card whose data could not be read
 * is the host's to say (a refusal is not an empty state); what this component
 * guarantees is that a card still loading, or genuinely empty, keeps the shape
 * it will have when full, and says so in words.
 */
export function DashboardCard({
  title, sub, icon, iconTone = 'brand', href, onOpen, count, actions, footer, variant = 'standard', tone = 'warn',
  state = 'ready', empty, loadingRows = 3, headingLevel = 3, testId, className, children, ...rest
}: DashboardCardProps) {
  const headingId = useId()
  const Heading = `h${headingLevel}` as 'h2' | 'h3' | 'h4'
  const linked = href != null || onOpen != null
  const titleControl = href != null
    ? <a className={s.open} href={href} onClick={onOpen}>{title}</a>
    : onOpen != null
      ? <button type="button" className={s.open} onClick={onOpen}>{title}</button>
      : title

  if (variant === 'attention') {
    return (
      <section
        className={cx(s.card, s.attention, s[tone], className)}
        data-dashboard-card="attention"
        data-tone={tone}
        data-testid={testId}
        aria-labelledby={headingId}
        {...rest}
      >
        <IconTile size="lg" tone={tone} className={s.mark} aria-hidden="true">{icon ?? MARK[tone]}</IconTile>
        <div className={s.copy}>
          <Heading id={headingId} className={s.title}>{title}</Heading>
          {sub != null && <p className={s.sentence}>{sub}</p>}
          {children}
        </div>
        {actions != null && <div className={s.actions}>{actions}</div>}
      </section>
    )
  }

  let body: ReactNode = children
  if (state === 'loading') {
    body = (
      <div className={cx(s.well, s.loading)} data-card-loading="">
        {/* Announced once, in words; the bars are decoration. */}
        <span className={s.srOnly} role="status">Loading…</span>
        {LINE.slice(0, Math.max(1, loadingRows)).map((width, index) => (
          <Skeleton key={index} width={width} height="var(--sp-3)" radius="var(--mat-r-pill)" />
        ))}
      </div>
    )
  } else if (state === 'empty') {
    body = (
      <EmptyState className={cx(s.well, s.empty)} action={empty?.action} data-card-empty="">
        <b className={s.emptyTitle}>{empty?.title ?? 'Nothing here yet.'}</b>
        {empty?.description != null && <p className={s.emptyText}>{empty.description}</p>}
      </EmptyState>
    )
  }

  return (
    <section
      className={cx(s.card, linked && s.linked, className)}
      data-dashboard-card="standard"
      data-linked={linked || undefined}
      data-state={state}
      data-testid={testId}
      aria-labelledby={headingId}
      aria-busy={state === 'loading' || undefined}
      {...rest}
    >
      <header className={s.head}>
        {icon != null && <IconTile tone={iconTone} className={s.mark} aria-hidden="true">{icon}</IconTile>}
        <div className={s.titles}>
          <Heading id={headingId} className={s.title}>{titleControl}</Heading>
          {sub != null && <p className={s.sub}>{sub}</p>}
        </div>
        {count != null && <CountPill className={s.count}>{count}</CountPill>}
        {actions != null && <div className={s.actions}>{actions}</div>}
      </header>
      {body != null && <div className={s.body}>{body}</div>}
      {footer != null && <div className={s.foot}>{footer}</div>}
    </section>
  )
}
